import { generateRadioButtonOptionsFromEnum } from 'components/final-form/radioUtils';
import React, { Component } from "react";
import { MessageSourceProps, withMessages } from 'react-message-source';
import { Button, Grid, Modal, RadioProps } from 'semantic-ui-react';
import { Field, Form as FinalForm, FormRenderProps } from 'react-final-form';
import {
  EntriesConsumerRenderProps,
  ExtendedBaseEntryDto,
  withEntriesContext
} from 'components/segments/entires/EntriesContext';
import Input from 'components/final-form/Input';
import RadioGroup from 'components/final-form/RadioGroup';
import { ProcedureType } from 'ts-types/api.enums';
import {
  composeValidators,
  mustBeNumber,
  mustBePositiveNumber,
  mustNotBeZero,
  required,
  validateOnlyIfPresent
} from 'utils/validatorUtils';
import FormModalContentContainer from 'components/layout/FormModalContentContainer';
import FormContainer from 'components/layout/FormContainer';
import FormContent from 'components/layout/FormContent';
import FormButtonsRow from 'components/layout/FormButtonsRow';
import PraxFormGrid from 'components/layout/PraxFormGrid';

interface SpecialProcedure {
  description: string,
  quantity: number,
  value: number,
  type: ProcedureType,
  rate: number
}

interface Props extends MessageSourceProps, EntriesConsumerRenderProps {
  modalOpen: boolean,
  onCloseModal: () => void,
  specialProcedure: ExtendedBaseEntryDto
}

interface State {
  procedureTypes: Array<RadioProps>
}

class SpecialProcedureModal extends Component<Props, State> {

  constructor(props: Props) {
    super(props);

    const {getMessage} = this.props;

    this.state = {
      procedureTypes: generateRadioButtonOptionsFromEnum(Object.values(ProcedureType), "procedureType", getMessage)
    }
  }

  handleSubmit = async (values: Partial<SpecialProcedure>) => {

    const {specialProcedure, saveEntry, onCloseModal} = this.props;

    let calculatedValue = 0;

    if (values.value && values.rate) {
      calculatedValue = +values.value * (+values.rate / 100);
    }

    const modifiedProcedure: ExtendedBaseEntryDto = {
      ...specialProcedure,
      description: values.description!,
      type: values.type ? values.type : specialProcedure.type,
      value: values.value ? +values.value : specialProcedure.value,
      percentage: values.rate ? +values.rate : undefined,
      calculatedValue: calculatedValue,
      quantity: values.quantity ? +values.quantity : 1
    };

    await saveEntry(modifiedProcedure);
    onCloseModal();
  };

  render(): React.ReactNode {

    const {modalOpen, onCloseModal, getMessage, specialProcedure} = this.props;

    let procedure: Partial<SpecialProcedure> = {
      type: ProcedureType.TP,
      rate: 100
    };

    if (specialProcedure) {
      procedure = {
        description: specialProcedure.description,
        value: specialProcedure.value,
        type: specialProcedure.type == ProcedureType.TP ? ProcedureType.TP : ProcedureType.CHF,
        rate: specialProcedure.percentage,
        quantity: specialProcedure.quantity
      }
    }

    return (
        <Modal
            open={modalOpen}
            onClose={onCloseModal}
            style={{width: "500px"}}
            closeOnDimmerClick={false}
        >
          <Modal.Header>{getMessage("specialProcedureModal.header")}</Modal.Header>
          <FormModalContentContainer>
            <FinalForm
                onSubmit={this.handleSubmit}
                subscription={{pristine: true, submitting: true}}
                initialValues={procedure}
                render={this.renderForm}
            />
          </FormModalContentContainer>
        </Modal>
    );
  };

  renderForm = ({submitting, handleSubmit}: FormRenderProps): React.ReactNode => {

    const {getMessage, onCloseModal} = this.props;
    const {procedureTypes} = this.state;

    const validator = validateOnlyIfPresent(composeValidators(mustBeNumber, mustBePositiveNumber, mustNotBeZero));

    return (
        <form onSubmit={handleSubmit}>
          <FormContainer>
            <FormContent>
              <PraxFormGrid>
                <Grid.Row>
                  <Grid.Column verticalAlign="middle" width={3}>{`${getMessage("description")}:`}</Grid.Column>
                  <Grid.Column verticalAlign="middle" width={13}>
                    <Field
                        name="description"
                        component={Input}
                        validate={required}
                        fluid
                    />
                  </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                  <Grid.Column verticalAlign="middle" width={3}>{`${getMessage("quantity")}:`}</Grid.Column>
                  <Grid.Column verticalAlign="middle" width={13}>
                    <Field
                        name="quantity"
                        component={Input}
                        validate={validator}
                        fluid
                    />
                  </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                  <Grid.Column verticalAlign="middle" width={3}>{`${getMessage("value")}:`}</Grid.Column>
                  <Grid.Column verticalAlign="middle" width={13}>
                    <Field
                        name="value"
                        component={Input}
                        validate={validator}
                        fluid
                    />
                  </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                  <Grid.Column verticalAlign="middle" width={3}>{`${getMessage("type")}:`}</Grid.Column>
                  <Grid.Column verticalAlign="middle" width={13}>
                    <Field
                        name="type"
                        component={RadioGroup}
                        radioDefinitions={procedureTypes}
                    />
                  </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                  <Grid.Column verticalAlign="middle" width={3}>{`${getMessage("rate")}:`}</Grid.Column>
                  <Grid.Column verticalAlign="middle" width={13}>
                    <Field
                        name="rate"
                        component={Input}
                        validate={validateOnlyIfPresent(composeValidators(mustBeNumber, mustBePositiveNumber))}
                        fluid
                    />
                  </Grid.Column>
                </Grid.Row>
              </PraxFormGrid>
            </FormContent>

            <FormButtonsRow>
              <Button compact type="submit" primary disabled={submitting}>{getMessage("button.save")}</Button>
              <Button compact type="button" disabled={submitting}
                      onClick={onCloseModal}>{getMessage("button.cancel")}</Button>
            </FormButtonsRow>
          </FormContainer>
        </form>
    )
  }
}

export default withMessages(withEntriesContext(SpecialProcedureModal));
